'use client';

import React, { useState, useMemo } from 'react';
import { Search, Filter, ChevronDown, Briefcase, Truck, ShoppingCart } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Link from 'next/link';

export type Job = {
  id: string;
  crmRef: string;
  name: string;
  client: string;
  receivedDate: string;
  deliveries: number;
  pos: number;
  sos: number;
  totalPoValue: string;
  totalSoValue: string;
  status: string;
  description: string;
};

const jobsData: Job[] = [
  { id: 'job-crm-9924', crmRef: 'JOB-CRM-9924', name: 'Merch drop — TechConf 2026', client: 'TechConf Events Ltd', receivedDate: '15/08/2026', deliveries: 4, pos: 4, sos: 4, totalPoValue: '£38,400', totalSoValue: '£46,080', status: 'active', description: 'Full branded merchandise drop for TechConf 2026: hoodies, tote bags, lanyards, and water bottles.' },
  { id: 'job-crm-9921', crmRef: 'JOB-CRM-9921', name: 'Branded hoodies — RetailCo Q3', client: 'RetailCo UK', receivedDate: '13/08/2026', deliveries: 2, pos: 2, sos: 2, totalPoValue: '£22,800', totalSoValue: '£27,360', status: 'active', description: 'Embroidered hoodies in three colourways for RetailCo Q3 staff uniform rollout.' },
  { id: 'job-crm-9919', crmRef: 'JOB-CRM-9919', name: 'Promo kit — SaaS Summit', client: 'SaaS Summit Ltd', receivedDate: '11/08/2026', deliveries: 6, pos: 6, sos: 6, totalPoValue: '£54,200', totalSoValue: '£65,040', status: 'active', description: 'Conference delegate promo kits including notebooks, pens, USB drives, and branded bags.' },
  { id: 'job-crm-9917', crmRef: 'JOB-CRM-9917', name: 'Tote bags — GreenFest', client: 'GreenFest CIC', receivedDate: '08/08/2026', deliveries: 3, pos: 3, sos: 3, totalPoValue: '£12,600', totalSoValue: '£15,120', status: 'active', description: 'Organic cotton tote bags with screen-printed logos for GreenFest sustainability event.' },
  { id: 'job-crm-9915', crmRef: 'JOB-CRM-9915', name: 'Embroidered polos — HealthOrg', client: 'HealthOrg NHS', receivedDate: '05/08/2026', deliveries: 5, pos: 5, sos: 5, totalPoValue: '£31,000', totalSoValue: '£37,200', status: 'active', description: 'NHS branded polo shirts for 5 regional health trusts, embroidered logo, 3 sizes.' },
  { id: 'job-crm-9912', crmRef: 'JOB-CRM-9912', name: 'Caps and beanies — SportsBrand', client: 'SportsBrand Ltd', receivedDate: '01/08/2026', deliveries: 3, pos: 3, sos: 3, totalPoValue: '£18,200', totalSoValue: '£21,840', status: 'active', description: 'Embroidered caps and beanies for SportsBrand winter launch campaign.' },
  { id: 'job-crm-9908', crmRef: 'JOB-CRM-9908', name: 'Desk kits — FinanceCorp', client: 'FinanceCorp PLC', receivedDate: '28/07/2026', deliveries: 2, pos: 2, sos: 2, totalPoValue: '£9,800', totalSoValue: '£11,760', status: 'complete', description: 'Premium desk kit bundles (notepad, pen set, cable organiser) for FinanceCorp new starters.' },
  { id: 'job-crm-9904', crmRef: 'JOB-CRM-9904', name: 'Branded water bottles — FitLife', client: 'FitLife Gyms', receivedDate: '22/07/2026', deliveries: 1, pos: 1, sos: 1, totalPoValue: '£7,200', totalSoValue: '£8,640', status: 'complete', description: 'Insulated stainless steel water bottles with laser-engraved logo for FitLife member welcome packs.' },
  { id: 'job-crm-9899', crmRef: 'JOB-CRM-9899', name: 'Event lanyards — MediaGroup', client: 'MediaGroup UK', receivedDate: '18/07/2026', deliveries: 2, pos: 2, sos: 2, totalPoValue: '£4,400', totalSoValue: '£5,280', status: 'complete', description: 'Full-colour dye-sublimation lanyards with safety breakaway for MediaGroup annual conference.' },
  { id: 'job-crm-9891', crmRef: 'JOB-CRM-9891', name: 'Promo pens — LegalFirm', client: 'Hartley & Stone LLP', receivedDate: '10/07/2026', deliveries: 1, pos: 1, sos: 1, totalPoValue: '£2,100', totalSoValue: '£2,520', status: 'cancelled', description: 'Engraved metal ballpoint pens — cancelled by client due to budget freeze.' },
  { id: 'job-crm-9887', crmRef: 'JOB-CRM-9887', name: 'Tees and hoodies — StartupFest', client: 'StartupFest Ltd', receivedDate: '05/07/2026', deliveries: 4, pos: 4, sos: 4, totalPoValue: '£28,400', totalSoValue: '£34,080', status: 'complete', description: 'Screen-printed event tees and zip hoodies for StartupFest 2026 speakers and staff.' },
  { id: 'job-crm-9881', crmRef: 'JOB-CRM-9881', name: 'Umbrellas — InsuranceCo', client: 'InsuranceCo Group', receivedDate: '01/07/2026', deliveries: 2, pos: 2, sos: 2, totalPoValue: '£11,600', totalSoValue: '£13,920', status: 'complete', description: 'Golf umbrellas with full-panel branded print for InsuranceCo client gifts.' },
];

const statusOptions = ['All statuses', 'Active', 'Complete', 'Cancelled'];
const clientOptions = ['All clients', ...Array.from(new Set(jobsData.map(j => j.client)))];

export default function JobsContent() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [clientFilter, setClientFilter] = useState('All clients');
  const [sortCol, setSortCol] = useState<keyof Job>('receivedDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const filtered = useMemo(() => {
    return jobsData
      .filter(j => {
        const matchSearch = !search || j.crmRef.toLowerCase().includes(search.toLowerCase()) || j.name.toLowerCase().includes(search.toLowerCase()) || j.client.toLowerCase().includes(search.toLowerCase());
        const matchStatus = statusFilter === 'All statuses' || j.status === statusFilter.toLowerCase();
        const matchClient = clientFilter === 'All clients' || j.client === clientFilter;
        return matchSearch && matchStatus && matchClient;
      })
      .sort((a, b) => {
        const av = a[sortCol] as string;
        const bv = b[sortCol] as string;
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
  }, [search, statusFilter, clientFilter, sortCol, sortDir]);

  const handleSort = (col: keyof Job) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: keyof Job }) => (
    <ChevronDown
      size={12}
      className="ml-1 inline transition-transform"
      style={{
        color: sortCol === col ? 'var(--accent)' : 'var(--slate)',
        transform: sortCol === col && sortDir === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    />
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--foreground)' }}>Jobs</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--slate)' }}>
            CRM-received jobs — {jobsData.length} total, {jobsData.filter(j => j.status === 'active').length} active
          </p>
        </div>
      </div>

      {/* Filters */}
      <div
        className="flex flex-wrap items-center gap-3 p-4 rounded-xl border"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div
          className="flex items-center gap-2 flex-1 min-w-[200px] px-3 py-2 rounded-md border"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)' }}
        >
          <Search size={14} style={{ color: 'var(--slate)' }} />
          <input
            type="text"
            placeholder="Search by job ID, name, or client…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 text-sm outline-none bg-transparent"
            style={{ color: 'var(--foreground)' }}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--slate)' }} />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm rounded-md border outline-none appearance-none cursor-pointer"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
            >
              {statusOptions.map(s => <option key={`status-opt-${s}`}>{s}</option>)}
            </select>
          </div>
          <select
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-md border outline-none appearance-none cursor-pointer"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)', maxWidth: '200px' }}
          >
            {clientOptions.map(c => <option key={`client-opt-${c}`}>{c}</option>)}
          </select>
        </div>
        <span className="text-xs ml-auto" style={{ color: 'var(--slate)' }}>
          {filtered.length} of {jobsData.length} jobs
        </span>
      </div>

      {/* Table */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--cloud)' }}>
                {[
                  { label: 'CRM ref', col: 'crmRef' as keyof Job },
                  { label: 'Job name', col: 'name' as keyof Job },
                  { label: 'Client', col: 'client' as keyof Job },
                  { label: 'Received', col: 'receivedDate' as keyof Job },
                  { label: 'Deliveries', col: 'deliveries' as keyof Job },
                  { label: 'POs', col: 'pos' as keyof Job },
                  { label: 'Total PO value', col: 'totalPoValue' as keyof Job },
                  { label: 'Total SO value', col: 'totalSoValue' as keyof Job },
                  { label: 'Status', col: 'status' as keyof Job },
                  { label: '', col: null },
                ].map((h, i) => (
                  <th
                    key={`th-jobs-${i}`}
                    className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                    style={{ color: 'var(--slate)', letterSpacing: '0.05em', cursor: h.col ? 'pointer' : 'default' }}
                    onClick={() => h.col && handleSort(h.col)}
                  >
                    {h.label}
                    {h.col && <SortIcon col={h.col} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-16">
                    <Briefcase size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>No jobs match your filters</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--slate)' }}>Jobs arrive automatically from your CRM via API</p>
                  </td>
                </tr>
              ) : (
                filtered.map((job) => (
                  <tr
                    key={job.id}
                    className="row-hover border-b"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <td className="px-4 py-3">
                      <Link href={`/jobs/${job.id}`} className="text-xs font-mono font-medium hover:underline" style={{ color: 'var(--accent)' }}>
                        {job.crmRef}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Link href={`/jobs/${job.id}`} className="font-medium hover:underline" style={{ color: 'var(--foreground)' }}>
                        {job.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span style={{ color: 'var(--slate)' }}>{job.client}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span style={{ color: 'var(--slate)' }}>{job.receivedDate}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Truck size={13} style={{ color: 'var(--slate)' }} />
                        <span className="tabular-nums font-medium" style={{ color: 'var(--foreground)' }}>{job.deliveries}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <ShoppingCart size={13} style={{ color: 'var(--slate)' }} />
                        <span className="tabular-nums font-medium" style={{ color: 'var(--foreground)' }}>{job.pos}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>{job.totalPoValue}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="tabular-nums font-semibold" style={{ color: 'var(--accent)' }}>{job.totalSoValue}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={job.status as never} />
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        href={`/jobs/${job.id}`}
                        className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors hover:bg-muted whitespace-nowrap"
                        style={{ borderColor: 'var(--border)', color: 'var(--slate)' }}
                      >
                        Open <ChevronDown size={12} className="rotate-[-90deg]" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          className="flex items-center justify-between px-4 py-3 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          <p className="text-xs" style={{ color: 'var(--slate)' }}>
            Showing {filtered.length} of {jobsData.length} jobs
          </p>
          <div className="flex items-center gap-1">
            {[1, 2].map(p => (
              <button
                key={`jobs-page-${p}`}
                className="w-7 h-7 text-xs rounded-md font-medium transition-colors"
                style={{
                  backgroundColor: p === 1 ? 'var(--ink)' : 'transparent',
                  color: p === 1 ? 'white' : 'var(--slate)',
                }}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--slate)' }}>Rows per page:</span>
            <select
              className="text-xs px-2 py-1 rounded-md border outline-none"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
            >
              {['15', '25', '50'].map(v => <option key={`jobs-rows-${v}`}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}